import {
    acknowledgeOrder,
    getBucketedStock,
    getSiteOrders,
    type SiteOrder,
} from '@api/vendor-portal';
import PageHeader from '@components/common/PageHeader';
import DeclareBatchesModal from '@components/containers/vendorPortal/DeclareBatchesModal';
import { toOrderLines } from '@components/containers/vendorPortal/batchGridRows';
import { buildOrderNudges } from '@components/containers/vendorPortal/nudges';
import NudgeStrip from '@components/ui/NudgeStrip';
import useToaster from '@hooks/toaster/useToaster';
import usePersistentState from '@hooks/usePersistentState';
import { Button, Group, Loader, SegmentedControl, Stack, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import JobCard from './JobCard';
import SkuCard from './SkuCard';
import pageStyles from './VendorHome.module.css';
import OpenOrdersBanner from './OpenOrdersBanner';
import OrderTotalsCards from './OrderTotalsCards';
import { buildOrderTotals } from './orderTotals';
import { buildSkuGroups, type SkuGroup } from './skuGroups';

/**
 * Which grain the job list is shown at.
 *
 * 'order' leads because it is what this page has always been, and flipping every site's morning
 * view without asking is not a default to change quietly. A site that thinks in products picks
 * 'sku' once and keeps it — see `usePersistentState` below.
 */
type HomeView = 'order' | 'sku';

/**
 * Where a contract manufacturer starts.
 *
 * ON THE JOBS, NEVER ON THE INVENTORY. The mental model on a production floor is "I have a job
 * to do"; stock is a by-product of doing it. A portal that opened on a balance sheet would be
 * asking somebody to translate from what they came here for into what the system happens to
 * store, every morning, before they could act.
 *
 * PRODUCING WITHOUT AN ORDER IS A FIRST-CLASS PATH, not a fallback hidden behind an empty
 * state. Sites really do run to stock, and a portal that only accepted production against an
 * order would push that work back onto paper — where the brand cannot see it at all.
 *
 * THE NUDGES ARE QUERIES OVER WHAT IS ON SCREEN, not configured alerts. Nothing has to be set
 * up, nothing arrives by email, and a site that fixes the thing sees the nudge go on the next
 * read. Alerts a brand configures are a different feature and a later one.
 */
const VendorHomeContainer: FC = () => {
    const { showToastSuccess, showToastError } = useToaster();
    const navigate = useNavigate();

    /**
    /** The end-of-shift path: a sheet of runs rather than one being recorded as it finishes. */
    const [declaringMany, setDeclaringMany] = useState(false);
    /**
     * The order a bulk declaration is being written against, or null for the page-header path.
     *
     * Kept apart from `declaringMany` on purpose: the two open the same grid but scope it
     * differently, and collapsing them into one flag would make "which order am I answering"
     * depend on how the modal happened to be opened.
     */
    const [declaringOrder, setDeclaringOrder] = useState<SiteOrder | null>(null);
    /**
     * The product a bulk declaration is being written against, when the page is grouped by SKU.
     *
     * A THIRD FLAG RATHER THAN A WIDENED SECOND, for the same reason `declaringOrder` is kept
     * apart from `declaringMany`: all three open the same grid, scoped differently, and folding
     * them together would make "what am I answering" depend on which button was pressed.
     */
    const [declaringSku, setDeclaringSku] = useState<SkuGroup | null>(null);
    const [acknowledging, setAcknowledging] = useState<string | null>(null);

    /**
     * REMEMBERED, NOT RESET. Somebody who works from products should not re-pick the view every
     * morning, and it is per browser so one person's preference is not imposed on the site.
     */
    const [view, setView] = usePersistentState<HomeView>('reparo.home.view', 'order');

    const {
        data: orderData,
        isLoading: ordersLoading,
        refetch: refetchOrders,
    } = useQuery(['vendor-orders'], getSiteOrders);

    // Finished goods only. Material balances were fetched for the stock nudges, which have
    // moved to the Stock screen where the batches they name actually are.
    const { data: productStock } = useQuery(['vendor-bucketed-stock', 'product'], () =>
        getBucketedStock({ itemType: 'product' }),
    );

    /*
     * MEMOISED FOR THE FALLBACK, not for the fetch. `?? []` mints a fresh empty array on every
     * render, so while the query is loading each of the three folds below saw a "new" input and
     * recomputed — harmless today, and exactly the shape that stops being harmless once one of
     * them is doing real work over a site's whole book.
     */
    const orders = useMemo(() => orderData?.orders ?? [], [orderData]);

    /**
     * Finished goods per SKU, as a plain count of what is standing here.
     *
     * IT USED TO CARRY A SENDABLE FIGURE TOO, to decide whether the card offered a Send button.
     * Shipping is the brand's act now — they release finished goods once they have passed them —
     * so the card offers nothing to gate, and a second number computed for a button that no
     * longer exists is just a claim nobody checks. The total stays because the site still wants
     * to know what is on its own shelf against the job.
     */
    const stockByProduct = useMemo(() => {
        const stock = new Map<string, { total: number }>();
        for (const item of productStock?.items ?? []) {
            stock.set(item.itemId, { total: item.total });
        }
        return stock;
    }, [productStock]);

    const totals = useMemo(() => buildOrderTotals(orders), [orders]);

    /*
     * The same lines the cards above are built from, filed under their product instead of their
     * order. Computed whichever view is on, because it is a fold over data already on the page
     * and gating it would buy nothing but a branch to get wrong.
     */
    const skuGroups = useMemo(() => buildSkuGroups(orders), [orders]);

    /**
     * Orders only. QC, expiry and blocked stock are conditions of the shelf, and they already
     * appear on the Stock screen beside the batches they name — repeating them here pushed the
     * job itself below a list of stock problems, which is the opposite of what this page is for.
     */
    const nudges = useMemo(() => buildOrderNudges(orders), [orders]);

    const handleAcknowledge = async (order: SiteOrder) => {
        // A job without an order header is keyed by its only work order.
        const key = order.productionOrderId ?? order.lines[0]?.workOrderId;
        if (!key) return;

        setAcknowledging(key);
        try {
            const result = await acknowledgeOrder({
                productionOrderId: order.productionOrderId,
                workOrderId: order.lines[0].workOrderId,
            });
            showToastSuccess({
                title: result.acknowledgedLines > 0 ? 'Acknowledged' : 'Already acknowledged',
                subtitle: 'The brand can see this site has picked the order up.',
            });
            void refetchOrders();
        } catch (err: any) {
            showToastError({
                title: 'Could not acknowledge it',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setAcknowledging(null);
        }
    };

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                className={pageStyles.header}
                icon="🏭"
                title="Today"
                subtitle="What this site has been asked to make, and what needs attention"
                actions={
                    <Group gap={8} wrap="nowrap">
                        {/*
                          * The same two words the cards below use, so the toggle names what it
                          * changes rather than describing a layout: the page is a list of
                          * orders, or a list of products. The figures are identical either way.
                          */}
                        <SegmentedControl
                            size="xs"
                            value={view}
                            onChange={(value) => setView(value as HomeView)}
                            data={[
                                { value: 'order', label: 'By order' },
                                { value: 'sku', label: 'By SKU' },
                            ]}
                        />
                        <Button
                            variant={ButtonVariant.ACTION}
                            // The grid, not the single form: recording from the page header
                            // means a sheet of runs rather than one job, and the grid does one
                            // row just as well. The single form is still what a card opens.
                            onClick={() => setDeclaringMany(true)}
                        >
                            Record production
                        </Button>
                    </Group>
                }
            />

            {!ordersLoading && <OpenOrdersBanner totals={totals} />}

            <NudgeStrip nudges={nudges} />

            {/* Below the nudges on purpose: those are exceptions that want acting on, this is
                standing context. The four figures and their order match the job cards, so the
                strip reads as the column totals of the list under it. */}
            {(ordersLoading || orders.length > 0) && (
                <OrderTotalsCards totals={totals} loading={ordersLoading} />
            )}

            {ordersLoading && (
                <Group gap={8}>
                    <Loader size="xs" />
                    <Text fz={13} c={neutralColors.textMuted}>
                        Loading orders…
                    </Text>
                </Group>
            )}

            {!ordersLoading && orders.length === 0 && (
                <Stack gap={8}>
                    <Text fz={15} fw={600} c={neutralColors.textPrimary}>
                        No open orders
                    </Text>
                    <Text fz={13} c={neutralColors.textSecondary}>
                        Nothing has been issued to this site yet. Production run anyway? Record it
                        — it does not need an order, and the brand sees the stock either way.
                    </Text>
                </Stack>
            )}

            <Stack gap={16}>
                {view === 'order'
                    ? orders.map((order) => (
                          <JobCard
                              key={order.productionOrderId ?? order.code}
                              order={order}
                              isAcknowledging={
                                  acknowledging ===
                                  (order.productionOrderId ?? order.lines[0]?.workOrderId)
                              }
                              onAcknowledge={() => handleAcknowledge(order)}
                              // Declaring FROM a job card names the job. Without it the card's
                              // "made" counter can never move off zero, however many runs are
                              // recorded, because that figure counts movements carrying this id.
                              stockByProduct={stockByProduct}
                              onDeclareAll={setDeclaringOrder}
                          />
                      ))
                    : skuGroups.map((group) => (
                          <SkuCard
                              key={group.productId}
                              group={group}
                              stockByProduct={stockByProduct}
                              acknowledging={acknowledging}
                              // The entry carries its own order, already narrowed to this
                              // product — acknowledging still names the whole order, because
                              // that is what the endpoint acknowledges.
                              onAcknowledge={(entry) => handleAcknowledge(entry.order)}
                              onDeclare={setDeclaringSku}
                          />
                      ))}
            </Stack>

            {/*
              * The same grid as the page header opens, scoped to one order: its lines only, so
              * the product picker offers that order's SKUs rather than the whole make-list and
              * Against is pinned to it with nothing else to pick. Opens with one blank row on
              * purpose — the old per-SKU seeding guessed the quantities, which invited
              * recording an order's whole remainder in one keystroke.
              */}
            <DeclareBatchesModal
                opened={!!declaringOrder}
                onClose={() => setDeclaringOrder(null)}
                onDeclared={() => void refetchOrders()}
                orderLines={toOrderLines(declaringOrder ? [declaringOrder] : [])}
                restrictProductsToOrder
                onRecordInward={() => navigate('/vendor/receive')}
                onReleaseQc={() => navigate('/vendor/rm-pm')}
            />

            {/*
              * The same grid again, scoped to one PRODUCT rather than one order.
              *
              * Every order wanting this SKU goes in, each already narrowed to that SKU's lines —
              * so `restrictProductsToOrder` collapses the picker to a single product while
              * Against still offers each order that could answer the run. That is the whole
              * reason Record production can sit on a product card: the person writes the number
              * they made, and says which order it answers row by row if it is not the default.
              */}
            <DeclareBatchesModal
                opened={!!declaringSku}
                onClose={() => setDeclaringSku(null)}
                onDeclared={() => void refetchOrders()}
                orderLines={toOrderLines(
                    (declaringSku?.lines ?? []).map((entry) => entry.order),
                )}
                restrictProductsToOrder
                onRecordInward={() => navigate('/vendor/receive')}
                onReleaseQc={() => navigate('/vendor/rm-pm')}
            />

            <DeclareBatchesModal
                opened={declaringMany}
                onClose={() => setDeclaringMany(false)}
                onDeclared={() => void refetchOrders()}
                orderLines={toOrderLines(orders)}
                // The hard cap: a site records production of what the brand has on order
                // here, nothing else. The lines are already every open order's, so the
                // picker collapses to exactly the products this site was asked to make.
                restrictProductsToOrder
                onRecordInward={() => navigate('/vendor/receive')}
                onReleaseQc={() => navigate('/vendor/rm-pm')}
            />

        </div>
    );
};

export default VendorHomeContainer;
